import React, { useState } from 'react';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Plus, Trash2, Check, Loader2, SkipForward, Sparkles
} from 'lucide-react';

// Guided Brand Voice interview (ROADMAP §4). Step 0 captures identity; steps 1-5
// gather typed, proprietary facts (repeatable, skippable); a review screen then
// creates the voice and bulk-saves the facts. Structured input only.

const TONES = ['professional', 'casual', 'friendly', 'authoritative', 'conversational', 'inspiring'];

// One screen per fact type. `required` keys must be filled for an entry to count.
// `list` fields are comma-separated in the UI and sent as arrays.
const FACT_STEPS = [
  {
    type: 'result',
    title: 'Results with real numbers',
    prompt: "Share measurable outcomes you've delivered. Real before/after numbers are what make content citable by AI.",
    fields: [
      { key: 'metric', label: 'Metric', placeholder: 'e.g. Organic traffic', required: true },
      { key: 'before', label: 'Before', placeholder: 'e.g. 2k/mo' },
      { key: 'after', label: 'After', placeholder: 'e.g. 18k/mo', required: true },
      { key: 'timeframe', label: 'Timeframe', placeholder: 'e.g. 6 months' },
      { key: 'context', label: 'Context', placeholder: 'e.g. B2B SaaS client' },
    ],
  },
  {
    type: 'pricing',
    title: 'Pricing',
    prompt: 'List your packages or price ranges and what each includes.',
    fields: [
      { key: 'package_name', label: 'Package', placeholder: 'e.g. Growth', required: true },
      { key: 'price', label: 'Price', placeholder: 'e.g. 1,500', required: true },
      { key: 'currency', label: 'Currency', placeholder: 'e.g. EUR' },
      { key: 'whats_included', label: "What's included", placeholder: 'e.g. 8 articles + strategy call', textarea: true },
    ],
  },
  {
    type: 'testimonial',
    title: 'Verbatim testimonials',
    prompt: 'Paste real client quotes exactly as written — Stilos reproduces them word for word, never paraphrased.',
    fields: [
      { key: 'quote', label: 'Quote', placeholder: '"Stilos 10x’d our qualified leads in a quarter."', required: true, textarea: true },
      { key: 'attribution', label: 'Attribution', placeholder: 'e.g. Maria K.' },
      { key: 'role_or_company', label: 'Role / Company', placeholder: 'e.g. CMO, Acme' },
    ],
  },
  {
    type: 'process',
    title: 'Unique process details',
    prompt: 'Name the methods, steps, or tools your business actually uses that generic sources would not know.',
    fields: [
      { key: 'method_name', label: 'Method name', placeholder: 'e.g. The AEO Sprint', required: true },
      { key: 'steps', label: 'Steps (comma-separated)', placeholder: 'audit, draft, optimize, publish', list: true },
      { key: 'tools', label: 'Tools (comma-separated)', placeholder: 'Ahrefs, Stilos', list: true },
    ],
  },
  {
    type: 'observation',
    title: 'First-hand market observations',
    prompt: "What do you see in your market that the generic sources don't say?",
    fields: [
      { key: 'claim', label: 'Observation', placeholder: 'e.g. AI engines cite listicles 3x more than prose', required: true, textarea: true },
      { key: 'evidence', label: 'Evidence', placeholder: 'e.g. internal 200-article sample' },
    ],
  },
];

const TOTAL_STEPS = 1 + FACT_STEPS.length + 1; // identity + facts + review

const emptyEntry = (fields) => Object.fromEntries(fields.map((f) => [f.key, '']));

const BrandVoiceInterview = ({ API, getAuthHeaders, onComplete, onCancel }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [identity, setIdentity] = useState({
    name: '', industry: '', target_audience: '',
    tone: 'professional', style: 'clear and concise', website_url: '',
  });

  // factData[type] = array of entry objects
  const [factData, setFactData] = useState(
    Object.fromEntries(FACT_STEPS.map((s) => [s.type, [emptyEntry(s.fields)]]))
  );

  const identityValid = identity.name.trim() && identity.industry.trim() && identity.target_audience.trim();

  const updateEntry = (type, idx, key, value) => {
    setFactData((prev) => {
      const next = { ...prev, [type]: prev[type].map((e, i) => (i === idx ? { ...e, [key]: value } : e)) };
      return next;
    });
  };
  const addEntry = (type, fields) =>
    setFactData((prev) => ({ ...prev, [type]: [...prev[type], emptyEntry(fields)] }));
  const removeEntry = (type, idx) =>
    setFactData((prev) => ({ ...prev, [type]: prev[type].filter((_, i) => i !== idx) }));

  // Build the bulk facts payload: keep only entries whose required keys are filled,
  // drop empty fields, split list fields into arrays.
  const buildFacts = () => {
    const facts = [];
    for (const s of FACT_STEPS) {
      for (const entry of factData[s.type]) {
        const requiredOk = s.fields.filter((f) => f.required).every((f) => (entry[f.key] || '').trim());
        if (!requiredOk) continue;
        const fields = {};
        for (const f of s.fields) {
          const raw = (entry[f.key] || '').trim();
          if (!raw) continue;
          fields[f.key] = f.list ? raw.split(',').map((x) => x.trim()).filter(Boolean) : raw;
        }
        facts.push({ fact_type: s.type, fields });
      }
    }
    return facts;
  };

  const factsCount = buildFacts().length;

  const next = () => { setError(''); setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1)); };
  const back = () => { setError(''); setStep((s) => Math.max(s - 1, 0)); };

  const handleFinish = async () => {
    setSaving(true);
    setError('');
    try {
      const voiceRes = await axios.post(`${API}/brand/voices`, identity, { headers: getAuthHeaders() });
      const voiceId = voiceRes.data.id;
      const facts = buildFacts();
      if (facts.length > 0) {
        await axios.post(`${API}/brand/voices/${voiceId}/facts/bulk`, { facts }, { headers: getAuthHeaders() });
      }
      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save brand voice');
      setSaving(false);
    }
  };

  const progressPct = Math.round((step / (TOTAL_STEPS - 1)) * 100);
  const isIdentity = step === 0;
  const isReview = step === TOTAL_STEPS - 1;
  const factStep = !isIdentity && !isReview ? FACT_STEPS[step - 1] : null;

  return (
    <div className="fade-in max-w-2xl" data-testid="brand-voice-interview">
      <button onClick={onCancel} className="flex items-center gap-2 text-[var(--color-muted)] hover:text-[var(--color-charcoal)] mb-6 text-sm transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Brand Voices
      </button>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-[var(--color-muted)]">Step {step + 1} of {TOTAL_STEPS}</span>
          {!isIdentity && <span className="text-xs text-[var(--color-muted)]">{factsCount} citable {factsCount === 1 ? 'asset' : 'assets'} so far</span>}
        </div>
        <div className="h-1.5 rounded-full bg-[var(--color-surface)] overflow-hidden">
          <div className="h-full bg-[var(--color-coral)] transition-all" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Step 0 — Identity */}
      {isIdentity && (
        <div className="card p-6 space-y-4" data-testid="interview-identity">
          <h1 className="font-serif text-2xl mb-1">Let's set up your brand voice</h1>
          <p className="text-sm text-[var(--color-muted)] mb-2">Start with the basics. You can refine everything else later under "Edit details".</p>
          <div>
            <label className="block text-xs font-medium mb-1.5">Brand Name *</label>
            <input type="text" value={identity.name} onChange={(e) => setIdentity({ ...identity, name: e.target.value })}
              className="input" placeholder="e.g., Stilos" data-testid="identity-name" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Industry *</label>
            <input type="text" value={identity.industry} onChange={(e) => setIdentity({ ...identity, industry: e.target.value })}
              className="input" placeholder="e.g., AI Content Technology, E-commerce" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Target Audience *</label>
            <textarea value={identity.target_audience} onChange={(e) => setIdentity({ ...identity, target_audience: e.target.value })}
              className="input min-h-[70px] resize-y" placeholder="Describe your ideal customer..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1.5">Tone</label>
              <select value={identity.tone} onChange={(e) => setIdentity({ ...identity, tone: e.target.value })} className="input">
                {TONES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Writing Style</label>
              <input type="text" value={identity.style} onChange={(e) => setIdentity({ ...identity, style: e.target.value })}
                className="input" placeholder="e.g., clear and concise" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Website URL</label>
            <input type="url" value={identity.website_url} onChange={(e) => setIdentity({ ...identity, website_url: e.target.value })}
              className="input" placeholder="https://example.com" />
          </div>
        </div>
      )}

      {/* Steps 1-5 — Facts */}
      {factStep && (
        <div data-testid={`interview-fact-${factStep.type}`}>
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-[var(--color-coral)]" />
              <h1 className="font-serif text-2xl">{factStep.title}</h1>
            </div>
            <p className="text-sm text-[var(--color-muted)]">{factStep.prompt}</p>
          </div>

          <div className="space-y-4">
            {factData[factStep.type].map((entry, idx) => (
              <div key={idx} className="card p-5 space-y-3" data-testid={`${factStep.type}-entry-${idx}`}>
                {factData[factStep.type].length > 1 && (
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-medium text-[var(--color-muted)] uppercase tracking-wide">Entry {idx + 1}</span>
                    <button onClick={() => removeEntry(factStep.type, idx)} className="btn btn-ghost p-1.5" title="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                {factStep.fields.map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium mb-1.5">{f.label}{f.required ? ' *' : ''}</label>
                    {f.textarea ? (
                      <textarea value={entry[f.key]} onChange={(e) => updateEntry(factStep.type, idx, f.key, e.target.value)}
                        className="input min-h-[70px] resize-y" placeholder={f.placeholder} />
                    ) : (
                      <input type="text" value={entry[f.key]} onChange={(e) => updateEntry(factStep.type, idx, f.key, e.target.value)}
                        className="input" placeholder={f.placeholder} />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <button onClick={() => addEntry(factStep.type, factStep.fields)}
            className="btn btn-secondary text-sm mt-3" data-testid={`add-${factStep.type}`}>
            <Plus className="w-4 h-4" /> Add another
          </button>
        </div>
      )}

      {/* Review */}
      {isReview && (
        <div className="card p-6" data-testid="interview-review">
          <h1 className="font-serif text-2xl mb-1">Review</h1>
          <p className="text-sm text-[var(--color-muted)] mb-5">
            <strong className="text-[var(--color-charcoal)]">{identity.name}</strong> · {identity.industry} · {identity.tone}
          </p>
          <div className="rounded-xl bg-[var(--color-surface)] p-4 mb-4 text-center">
            <div className="font-serif text-3xl text-[var(--color-coral)]">{factsCount}</div>
            <div className="text-xs text-[var(--color-muted)]">citable {factsCount === 1 ? 'asset' : 'assets'} ready to weave into content</div>
          </div>
          <div className="space-y-1.5">
            {FACT_STEPS.map((s) => {
              const count = buildFacts().filter((f) => f.fact_type === s.type).length;
              return (
                <div key={s.type} className="flex items-center justify-between text-sm py-1.5 border-b border-[var(--color-border)] last:border-0">
                  <span>{s.title}</span>
                  <span className="text-[var(--color-muted)]">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-500 mt-4">{error}</p>}

      {/* Nav */}
      <div className="flex gap-3 mt-6">
        {step > 0 && (
          <button onClick={back} disabled={saving} className="btn btn-secondary" data-testid="interview-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}
        <div className="flex-1" />
        {factStep && (
          <button onClick={next} className="btn btn-ghost text-sm" data-testid="interview-skip">
            <SkipForward className="w-4 h-4" /> Skip
          </button>
        )}
        {isIdentity && (
          <button onClick={next} disabled={!identityValid} className="btn btn-primary" data-testid="interview-next">
            Next <ArrowRight className="w-4 h-4" />
          </button>
        )}
        {factStep && (
          <button onClick={next} className="btn btn-primary" data-testid="interview-next">
            Next <ArrowRight className="w-4 h-4" />
          </button>
        )}
        {isReview && (
          <button onClick={handleFinish} disabled={saving} className="btn btn-primary" data-testid="interview-finish">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Create Brand Voice</>}
          </button>
        )}
      </div>
    </div>
  );
};

export default BrandVoiceInterview;
